import { useState, useEffect, useCallback } from 'preact/hooks';
import Button from '../components/shared/Button.jsx';
import Card from '../components/shared/Card.jsx';

// De partij die factureert. Alles hier komt op elke factuur terecht, dus het
// scherm zegt onomwonden wat er nog ontbreekt in plaats van dat pas te laten
// blijken op het moment dat je wilt factureren.
export default function SettingsView() {
  const [entity, setEntity] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);

  const refresh = useCallback(async () => {
    const res = await fetch('/api/billing-entity', { credentials: 'same-origin' });
    if (!res.ok) return;
    const data = await res.json();
    setEntity(data.entity);
    setForm(toForm(data.entity));
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  function set(field, value) {
    if (error) setError(null);
    if (saved) setSaved(false);
    setForm(f => ({ ...f, [field]: value }));
  }

  async function submit(e) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res = await fetch('/api/billing-entity', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          ...form,
          invoice_number_start: Number(form.invoice_number_start),
          payment_term_days: Number(form.payment_term_days),
          vat_rate_percent: Number(form.vat_rate_percent)
        })
      });
      const data = res.headers.get('content-type')?.includes('application/json') ? await res.json() : {};
      if (!res.ok) throw new Error(firstIssue(data) || data.error || 'save_failed');
      setEntity(data.entity);
      setForm(toForm(data.entity));
      setSaved(true);
    } catch (err) {
      setError(translate(err.message));
    } finally {
      setBusy(false);
    }
  }

  if (!form) {
    return <Card><div class="empty-state muted">Instellingen laden…</div></Card>;
  }

  const year = new Date().getFullYear();
  const firstNumber = `${form.invoice_number_prefix}${year}${String(form.invoice_number_start).padStart(4, '0')}`;

  return (
    <div class="settings-tab">
      <div class="dashboard-section-head">
        <h2>Facturerende partij</h2>
      </div>

      {!entity.isComplete && (
        <div class="form-warning" role="status">
          <strong>Nog niet compleet.</strong> Zonder {listMissing(entity.missingFields)} kan
          er geen geldige factuur worden opgesteld. Vul dat hieronder aan.
        </div>
      )}

      {saved && (
        <div class="inline-notice" role="status">
          Opgeslagen.
          <button type="button" class="inline-notice-close" onClick={() => setSaved(false)} aria-label="Melding sluiten">&times;</button>
        </div>
      )}

      <Card>
        <form onSubmit={submit} class="app-form">
          <p class="muted small">
            4U is een handelsnaam, geen rechtspersoon, en kan dus niet zelf factureren.
            De factuur komt van de BV; de handelsnaam staat er als merk naast.
          </p>

          <div class="form-row-pair">
            <label style={{ flex: 2 }}>
              <span>Statutaire naam</span>
              <input value={form.legal_name} onInput={e => set('legal_name', e.currentTarget.value)} required maxLength={160} />
            </label>
            <label>
              <span>Handelsnaam</span>
              <input value={form.trade_name} onInput={e => set('trade_name', e.currentTarget.value)} maxLength={160} />
            </label>
          </div>

          <div class="form-row-pair">
            <label>
              <span>KvK-nummer</span>
              <input value={form.kvk_nr} onInput={e => set('kvk_nr', e.currentTarget.value)} pattern="\d{8}" placeholder="12345678" />
            </label>
            <label>
              <span>BTW-nummer</span>
              <input value={form.btw_nr} onInput={e => set('btw_nr', e.currentTarget.value)} placeholder="NL123456789B01" />
            </label>
          </div>

          <div class="form-row-pair">
            <label style={{ flex: 2 }}>
              <span>Straat</span>
              <input value={form.address_street} onInput={e => set('address_street', e.currentTarget.value)} />
            </label>
            <label>
              <span>Huisnr</span>
              <input value={form.address_house_nr} onInput={e => set('address_house_nr', e.currentTarget.value)} />
            </label>
          </div>

          <div class="form-row-pair">
            <label>
              <span>Postcode</span>
              <input value={form.address_postcode} onInput={e => set('address_postcode', e.currentTarget.value)} placeholder="1234 AB" />
            </label>
            <label>
              <span>Plaats</span>
              <input value={form.address_city} onInput={e => set('address_city', e.currentTarget.value)} />
            </label>
          </div>

          <div class="form-row-pair">
            <label style={{ flex: 2 }}>
              <span>IBAN</span>
              <input value={form.iban} onInput={e => set('iban', e.currentTarget.value)} placeholder="NL13 KNAB 0257 8109 27" />
            </label>
            <label style={{ flex: 2 }}>
              <span>Tenaamstelling</span>
              <input value={form.iban_holder} onInput={e => set('iban_holder', e.currentTarget.value)} maxLength={160} />
            </label>
          </div>
          {entity.ibanFormatted && (
            <p class="muted small">Opgeslagen als <strong>{entity.ibanFormatted}</strong> — controlegetal klopt.</p>
          )}

          <h4 class="form-section-head">Facturatie</h4>

          <div class="form-row-pair">
            <label>
              <span>Voorvoegsel</span>
              <input value={form.invoice_number_prefix} onInput={e => set('invoice_number_prefix', e.currentTarget.value.toUpperCase())} maxLength={10} />
            </label>
            <label>
              <span>Eerste volgnummer per jaar</span>
              <input type="number" min="1" max="9999" value={form.invoice_number_start} onInput={e => set('invoice_number_start', e.currentTarget.value)} />
            </label>
          </div>
          <p class="muted small">
            De eerste factuur van {year} krijgt nummer <strong>{firstNumber}</strong>.
            De teller begint elk kalenderjaar opnieuw bij dit volgnummer.
          </p>

          <div class="form-row-pair">
            <label>
              <span>Betaaltermijn (dagen)</span>
              <input type="number" min="1" max="180" value={form.payment_term_days} onInput={e => set('payment_term_days', e.currentTarget.value)} />
            </label>
            <label>
              <span>BTW-tarief (%)</span>
              <input type="number" min="0" max="100" value={form.vat_rate_percent} onInput={e => set('vat_rate_percent', e.currentTarget.value)} />
            </label>
          </div>

          {error && <div class="form-error">{error}</div>}
          <div class="form-actions">
            <Button type="submit" disabled={busy}>{busy ? 'Opslaan…' : 'Opslaan'}</Button>
          </div>
        </form>
      </Card>
    </div>
  );
}

function toForm(entity) {
  return {
    legal_name: entity.legalName || '',
    trade_name: entity.tradeName || '',
    kvk_nr: entity.kvkNr || '',
    btw_nr: entity.btwNr || '',
    address_street: entity.address.street || '',
    address_house_nr: entity.address.houseNr || '',
    address_postcode: entity.address.postcode || '',
    address_city: entity.address.city || '',
    iban: entity.ibanFormatted || '',
    iban_holder: entity.ibanHolder || '',
    invoice_number_prefix: entity.invoiceNumberPrefix,
    invoice_number_start: String(entity.invoiceNumberStart),
    payment_term_days: String(entity.paymentTermDays),
    vat_rate_percent: String(entity.vatRatePercent)
  };
}

function listMissing(fields) {
  if (fields.length === 1) return `een ${fields[0]}`;
  return `${fields.slice(0, -1).join(', ')} en ${fields[fields.length - 1]}`;
}

// Why: zod meldt de eerste inhoudelijke fout in issues[0].message. Die is
// specifieker dan het generieke invalid_input en zegt de gebruiker wél iets.
function firstIssue(data) {
  return Array.isArray(data?.issues) && data.issues.length > 0 ? data.issues[0].message : null;
}

function translate(code) {
  const messages = {
    invalid_input: 'Een of meer velden zijn ongeldig.',
    invalid_kvk: 'KvK-nummer moet 8 cijfers zijn.',
    invalid_btw: 'BTW-nummer moet de vorm NL123456789B01 hebben.',
    invalid_postcode: 'Postcode moet 4 cijfers + 2 letters zijn (bv. 1234 AB).',
    invalid_iban: 'Dit IBAN klopt niet — het controlegetal komt niet uit. Controleer op een omgewisseld cijfer.',
    invalid_prefix: 'Voorvoegsel mag alleen hoofdletters en cijfers bevatten.',
    save_failed: 'Opslaan is mislukt.'
  };
  return messages[code] || 'Er ging iets mis. Probeer opnieuw.';
}
